import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { ApiPage } from '../types';

// Keys
export const pageKeys = {
    all: ['pages'] as const,
    lists: () => [...pageKeys.all, 'list'] as const,
    list: (filters: string) => [...pageKeys.lists(), { filters }] as const,
    details: () => [...pageKeys.all, 'detail'] as const,
    detail: (idOrSlug: string) => [...pageKeys.details(), idOrSlug] as const,
};

// --- Hooks ---

export const usePages = () => {
    return useQuery({
        queryKey: pageKeys.lists(),
        queryFn: async () => {
            const { data } = await api.get<ApiPage[]>('/api/admin/pages');
            return data || [];
        },
    });
};

export const usePage = (slug: string) => {
    return useQuery({
        queryKey: pageKeys.detail(slug),
        queryFn: async () => {
            const { data } = await api.get<ApiPage>(`/api/pages/${slug}`);
            return data;
        },
        enabled: !!slug,
    });
};

export const useAdminPage = (id: string) => {
    return useQuery({
        queryKey: pageKeys.detail(id),
        queryFn: async () => {
            const { data } = await api.get<ApiPage>(`/api/admin/pages/${id}`);
            return data;
        },
        enabled: !!id,
    });
};

export const useCreatePage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (newPage: Partial<ApiPage>) => {
            const { data } = await api.post<ApiPage>('/api/admin/pages', newPage);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: pageKeys.lists() });
        },
    });
};

export const useUpdatePage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, ...data }: Partial<ApiPage> & { id: string }) => {
            const { data: res } = await api.put<ApiPage>(`/api/admin/pages/${id}`, data);
            return res;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: pageKeys.lists() });
            queryClient.invalidateQueries({ queryKey: pageKeys.detail(data.id) });
            queryClient.invalidateQueries({ queryKey: pageKeys.detail(data.slug) });
        },
    });
};

export const useDeletePage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/api/admin/pages/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: pageKeys.lists() });
        },
    });
};
